import { PartialNullable } from "../../Extensions";
import { IRadObject, Property, RadObjectConstructor } from "../../RadObject";
import { DialogResultEnum } from "../Dialogs/DialogResultEnum";
import { Accordion } from "./Accordion";
import { ButtonStyleEnum } from "./ButtonStyleEnum";
import { ClassEditor } from "./ClassEditor";
import { Dialog, DialogPropertyValues, IDialog } from "./Dialog";
import { NAMESPACE } from "./_Namespace";
import { PropertyEditor } from "./PropertyEditor";

import "./ClassPropertyDialog.scss";

export type ClassPropertyDialogPropertyValues =
    DialogPropertyValues
    & {
        informationText: { description: string, title: string };
    };

export type ClassPropertyDialogProperties = {
    [name in keyof ClassPropertyDialogPropertyValues]: Property<ClassPropertyDialogPropertyValues[name]>
};

export interface IClassPropertyDialog extends IDialog, ClassPropertyDialogProperties
{
}

export class ClassPropertyDialog extends Dialog implements IClassPropertyDialog
{
    // #region Static

    public static override classNamespace = NAMESPACE + "ClassPropertyDialog";
    public static override className = "ClassPropertyDialog";

    // #region Overrides

    public static override createPropertyDefaults(): PartialNullable<ClassPropertyDialogPropertyValues>
    {
        return {
            ...super.createPropertyDefaults(),
            ...{
                buttonStyle: ButtonStyleEnum.okCancel,
                cancelButtonText: "Close",
                okButtonText: "Save",
            }
        };
    }

    // #endregion

    // #endregion

    // #region Instance

    // #region Global Variables

    public instance?: IRadObject;

    protected accordion?: Accordion;
    protected settingDataSource?: boolean;

    private _classEditor?: ClassEditor;

    // #endregion

    // #region Properties

    public informationText!: Property<{ description: string, title: string }>;

    // #endregion

    // #region Constructor

    constructor(propertyValues?: PartialNullable<ClassPropertyDialogPropertyValues>)
    {
        super(propertyValues);

        this.addCssClass("rad-control-class-property-dialog");
        this.detach();
    }

    // #endregion

    // #region Public Methods

    public save()
    {
        if (this.instance && this._classEditor)
            this._classEditor.save(this.instance);
    }

    // #endregion

    // #region Overrides

    protected override async _activateAsync()
    {
        await super._activateAsync();

        await Promise.all(
            this.controlsGet<PropertyEditor>(true, PropertyEditor)
                .map(item => item.loadDataAsync())
        );
    }

    public override async closeAsync()
    {
        if (this.dialogResult === DialogResultEnum.ok)
            this.save();

        await super.closeAsync();
        this.openPromise?.resolve({ dialogResult: DialogResultEnum.none });
    }
    public override async openAsync(instance: IRadObject)
    {
        if (!this._classEditor || this.instance !== instance)
        {
            this.instance = instance;

            this._classEditor?.remove();
            this._classEditor = (await ClassEditor.createAsync(instance)).appendTo(this);
            this.title.set((Object.getPrototypeOf(instance).constructor as RadObjectConstructor).className ?? (Object.getPrototypeOf(instance).constructor as RadObjectConstructor).name);
        }

        this._classEditor.load(instance);

        return await super.openAsync();
    }

    // #endregion

    // #endregion
}

